import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        {/* Header */}
        <div className="border-b border-gray-200 pb-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy for My-Healthrecord
          </h1>
          <p className="text-sm text-gray-600">
            Last updated: December 13, 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="prose max-w-none mb-8">
          <p className="text-gray-700 mb-4">
            This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
          </p>
          <p className="text-gray-700 mb-4">
            We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </div>

        {/* Interpretation and Definitions */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Interpretation and Definitions</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Interpretation</h3>
          <p className="text-gray-700 mb-4">
            The words whose initial letters are capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">Definitions</h3>
          <p className="text-gray-700 mb-3">For the purposes of this Privacy Policy:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
            <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
            <li><strong>Affiliate</strong> means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
            <li><strong>Application</strong> refers to My-Healthrecord, the software program provided by the Company.</li>
            <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to My-Healthrecord.</li>
            <li><strong>Country</strong> refers to: Andhra Pradesh, India</li>
            <li><strong>Device</strong> means any device that can access the Service such as a computer, a cell phone or a digital tablet.</li>
            <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
            <li><strong>Service</strong> refers to the Application.</li>
            <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company.</li>
            <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.</li>
            <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
          </ul>
        </section>

        {/* Collecting and Using Personal Data */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Collecting and Using Your Personal Data</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Types of Data Collected</h3>
          
          <h4 className="text-lg font-semibold text-gray-800 mb-2">Personal Data</h4>
          <p className="text-gray-700 mb-3">
            While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
            <li>First name and last name</li>
            <li>Phone number</li>
            <li>Usage Data</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-2">Usage Data</h4>
          <p className="text-gray-700 mb-4">
            Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
          </p>
          <p className="text-gray-700 mb-4">
            When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device's unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.
          </p>
        </section>

        {/* Use of Personal Data */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Use of Your Personal Data</h3>
          <p className="text-gray-700 mb-3">The Company may use Personal Data for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
            <li><strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.</li>
            <li><strong>To manage Your Account</strong>: to manage Your registration as a user of the Service.</li>
            <li><strong>For the performance of a contract</strong>: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased.</li>
            <li><strong>To contact You</strong>: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.</li>
            <li><strong>To provide You with news, special offers</strong>, and general information about other goods, services and events.</li>
            <li><strong>To manage Your requests</strong>: To attend and manage Your requests to Us.</li>
            <li><strong>For business transfers</strong>: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets.</li>
            <li><strong>For other purposes</strong>: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns.</li>
          </ul>

          <p className="text-gray-700 mb-3">We may share Your personal information in the following situations:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
            <li><strong>With Service Providers</strong>: We may share Your personal information with Service Providers to monitor and analyze the use of our Service.</li>
            <li><strong>For business transfers</strong>: We may share or transfer Your personal information in connection with any merger, sale of Company assets, financing, or acquisition.</li>
            <li><strong>With Affiliates</strong>: We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy.</li>
            <li><strong>With business partners</strong>: We may share Your information with Our business partners to offer You certain products, services or promotions.</li>
            <li><strong>With other users</strong>: when You share personal information or otherwise interact in the public areas with other users.</li>
            <li><strong>With Your consent</strong>: We may disclose Your personal information for any other purpose with Your consent.</li>
          </ul>
        </section>

        {/* Retention */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Retention of Your Personal Data</h3>
          <p className="text-gray-700 mb-4">
            The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
          </p>
        </section>

        {/* Transfer */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Transfer of Your Personal Data</h3>
          <p className="text-gray-700 mb-4">
            Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.
          </p>
          <p className="text-gray-700 mb-4">
            The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy.
          </p>
        </section>

        {/* Delete */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Delete Your Personal Data</h3>
          <p className="text-gray-700 mb-4">
            You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You. Our Service may give You the ability to delete certain information about You from within the Service.
          </p>
          <p className="text-gray-700 mb-4">
            You may update, amend, or delete Your information at any time by signing in to Your Account and visiting the account settings section. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.
          </p>
        </section>

        {/* Disclosure */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Disclosure of Your Personal Data</h3>
          
          <h4 className="text-lg font-semibold text-gray-800 mb-2">Business Transactions</h4>
          <p className="text-gray-700 mb-4">
            If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.
          </p>

          <h4 className="text-lg font-semibold text-gray-800 mb-2">Law Enforcement</h4>
          <p className="text-gray-700 mb-4">
            Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities.
          </p>

          <h4 className="text-lg font-semibold text-gray-800 mb-2">Other Legal Requirements</h4>
          <p className="text-gray-700 mb-3">The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
            <li>Comply with a legal obligation</li>
            <li>Protect and defend the rights or property of the Company</li>
            <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
            <li>Protect the personal safety of Users of the Service or the public</li>
            <li>Protect against legal liability</li>
          </ul>
        </section>

        {/* Security */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Security of Your Personal Data</h3>
          <p className="text-gray-700 mb-4">
            The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially reasonable means to protect Your Personal Data, We cannot guarantee its absolute security.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Children's Privacy</h3>
          <p className="text-gray-700 mb-4">
            Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
          </p>
        </section>

        {/* Links */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Links to Other Websites</h3>
          <p className="text-gray-700 mb-4">
            Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.
          </p>
        </section>

        {/* Changes */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Changes to this Privacy Policy</h3>
          <p className="text-gray-700 mb-4">
            We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page. We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.
          </p>
          <p className="text-gray-700 mb-4">
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>
        </section>

        {/* Contact */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Contact Us</h3>
          <p className="text-gray-700 mb-3">If you have any questions about this Privacy Policy, You can contact us:</p>
          <ul className="list-disc pl-6 text-gray-700">
            <li>By email: <a href="mailto:raviraj.akula@gmail.com" className="text-blue-600 hover:text-blue-800 underline">raviraj.akula@gmail.com</a></li>
          </ul>
        </section>

        {/* Footer */}
        <div className="border-t border-gray-200 pt-6 mt-8">
          <p className="text-sm text-gray-500 text-center">
            © 2025 My-Healthrecord. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
